import React, { useEffect, useState, useCallback } from "react";
import { api } from "@/lib/api";
import { Skeleton } from "@/components/EmptyState";
import {
  CalendarClock, AlertTriangle, Truck, ShoppingCart, ChevronRight, Timer, Package,
} from "lucide-react";
import { Link, useNavigate } from "react-router-dom";
import {
  Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription, SheetTrigger,
} from "@/components/ui/sheet";

function fmtDate(s) { if (!s) return "—"; try { return new Date(s).toLocaleDateString(undefined, { month: "short", day: "numeric" }); } catch { return s; } }

export function DashboardWidgets() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  const fetchData = useCallback(async () => {
    try {
      const { data } = await api.get("/dashboard/widgets");
      setData(data);
    } catch (e) { /* soft fail — other dashboard sections still work */ }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { fetchData(); }, [fetchData]);
  useEffect(() => { const id = setInterval(fetchData, 60_000); return () => clearInterval(id); }, [fetchData]);

  if (loading && !data) {
    return (
      <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-3">
        {[...Array(4)].map((_, i) => <Skeleton key={i} className="h-48" />)}
      </div>
    );
  }
  if (!data) return null;

  return (
    <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-3">
      <UpcomingCard upcoming={data.upcoming} />
      <OverdueCard overdue={data.overdue} />
      <TopSuppliersCard top={data.top_suppliers} />
      <ReorderSoonCard reorder={data.reorder_soon} />
    </div>
  );
}

// ---------------- Upcoming ----------------
function UpcomingCard({ upcoming }) {
  const items = (upcoming?.items || []).slice(0, 4);
  return (
    <WidgetCard
      testid="widget-upcoming"
      icon={CalendarClock}
      iconTone="text-primary"
      title="Upcoming production"
      subtitle={`Next ${upcoming?.window_days || 14} days`}
      count={upcoming?.count || 0}
      href="/app/production-calendar"
      empty="No jobs planned."
    >
      {items.map((o) => (
        <div key={o.id} className="flex items-center justify-between py-1.5 border-b last:border-0 border-border" data-testid={`widget-upcoming-row-${o.id}`}>
          <div className="min-w-0 flex-1">
            <div className="font-medium text-sm truncate">{o.customer}</div>
            <div className="text-[11px] text-muted-foreground font-mono">{o.order_number} • {o.total_quantity} units</div>
          </div>
          <div className="text-xs font-medium text-primary shrink-0">{fmtDate(o.production_date)}</div>
        </div>
      ))}
    </WidgetCard>
  );
}

// ---------------- Overdue ----------------
function OverdueCard({ overdue }) {
  const items = (overdue?.items || []).slice(0, 4);
  const tone = overdue?.count > 0 ? "text-rose-600 dark:text-rose-400" : "text-muted-foreground";
  return (
    <WidgetCard
      testid="widget-overdue"
      icon={AlertTriangle}
      iconTone={tone}
      title="Overdue production"
      subtitle={overdue?.count === 0 ? "On track 🎉" : "Past planned date"}
      count={overdue?.count || 0}
      countTone={overdue?.count > 0 ? "text-rose-600 dark:text-rose-400" : undefined}
      href="/app/production-calendar"
      empty="Nothing is overdue."
    >
      {items.map((o) => (
        <div key={o.id} className="flex items-center justify-between py-1.5 border-b last:border-0 border-border" data-testid={`widget-overdue-row-${o.id}`}>
          <div className="min-w-0 flex-1">
            <div className="font-medium text-sm truncate">{o.customer}</div>
            <div className="text-[11px] text-muted-foreground font-mono">{o.order_number} • {o.total_quantity} units</div>
          </div>
          <div className="text-xs font-medium text-rose-600 dark:text-rose-400 shrink-0">{o.days_overdue}d late</div>
        </div>
      ))}
    </WidgetCard>
  );
}

// ---------------- Top Suppliers ----------------
function TopSuppliersCard({ top }) {
  const items = (top?.items || []).slice(0, 4);
  const max = Math.max(...items.map((x) => x.total_qty || 0), 1);
  return (
    <WidgetCard
      testid="widget-top-suppliers"
      icon={Truck}
      iconTone="text-primary"
      title="Top suppliers"
      subtitle={`By qty received (${top?.window_days || 90}d)`}
      count={top?.count || 0}
      href="/app/reports/supplier-activity"
      empty="No supplier intakes."
    >
      {items.map((s) => (
        <div key={s.supplier_id} className="py-1.5 border-b last:border-0 border-border" data-testid={`widget-supplier-row-${s.supplier_id}`}>
          <div className="flex items-center justify-between gap-2">
            <div className="font-medium text-sm truncate">{s.name}</div>
            <div className="text-xs font-mono tabular-nums text-primary shrink-0">{s.total_qty}</div>
          </div>
          <div className="h-1.5 rounded-full bg-muted mt-1 overflow-hidden">
            <div className="h-full bg-primary" style={{ width: `${Math.min(100, (s.total_qty / max) * 100)}%` }} />
          </div>
          <div className="flex items-center justify-between text-[11px] text-muted-foreground mt-0.5">
            <span>{s.intakes} intake{s.intakes === 1 ? "" : "s"}</span>
            {(s.lead_time_label || s.lead_time_days) && (
              <span className="inline-flex items-center gap-1">
                <Timer className="w-3 h-3" /> {s.lead_time_label || `${s.lead_time_days}d`}
              </span>
            )}
          </div>
        </div>
      ))}
    </WidgetCard>
  );
}

// ---------------- Reorder Soon ----------------
function ReorderSoonCard({ reorder }) {
  const counts = reorder?.counts || { critical: 0, warning: 0, watch: 0 };
  const total = counts.critical + counts.warning + counts.watch;
  const [open, setOpen] = useState(false);
  const preview = [...(reorder?.critical || []), ...(reorder?.warning || [])].slice(0, 4);
  const tone = counts.critical > 0
    ? "text-rose-600 dark:text-rose-400"
    : counts.warning > 0
    ? "text-amber-600 dark:text-amber-400"
    : "text-muted-foreground";
  return (
    <Sheet open={open} onOpenChange={setOpen}>
      <SheetTrigger asChild>
        <button
          type="button"
          className="text-left rounded-2xl border border-border bg-card p-4 hover:bg-accent/30 transition-colors"
          data-testid="widget-reorder-soon"
        >
          <div className="flex items-center gap-2 mb-2 text-sm font-medium text-muted-foreground">
            <ShoppingCart className={`w-4 h-4 ${tone}`} /> Reorder soon
            <ChevronRight className="w-4 h-4 ml-auto text-muted-foreground" />
          </div>
          <div className="flex items-baseline gap-3">
            <div className={`text-3xl font-semibold tabular-nums ${tone}`} data-testid="widget-reorder-count">{total}</div>
            <div className="flex gap-1 text-[11px]">
              {counts.critical > 0 && <span className="px-1.5 py-0.5 rounded-full bg-rose-500/10 text-rose-600 dark:text-rose-400 border border-rose-500/30">{counts.critical} critical</span>}
              {counts.warning > 0 && <span className="px-1.5 py-0.5 rounded-full bg-amber-500/10 text-amber-600 dark:text-amber-400 border border-amber-500/30">{counts.warning} warn</span>}
              {counts.watch > 0 && <span className="px-1.5 py-0.5 rounded-full bg-muted">{counts.watch} watch</span>}
            </div>
          </div>
          <div className="mt-2 text-[11px] text-muted-foreground">Lead-time aware • last {reorder?.lookback_days || 30}d pace</div>
          <div className="mt-3 space-y-1">
            {preview.length === 0 ? (
              <div className="text-xs text-muted-foreground py-2">Nothing needs reorder now.</div>
            ) : preview.map((it) => (
              <div key={it.item_id} className="flex items-center justify-between text-xs py-0.5">
                <span className="truncate min-w-0 flex-1">{it.name}</span>
                <span className="font-mono text-muted-foreground shrink-0 ml-2">{it.days_of_cover}d</span>
              </div>
            ))}
          </div>
        </button>
      </SheetTrigger>
      <SheetContent side="right" className="w-full sm:max-w-lg overflow-y-auto" data-testid="widget-reorder-sheet">
        <SheetHeader>
          <SheetTitle className="flex items-center gap-2"><ShoppingCart className="w-5 h-5 text-primary" /> Reorder soon</SheetTitle>
          <SheetDescription>
            Uses each item&apos;s supplier lead time and last {reorder?.lookback_days || 30} days of usage to warn before stock runs out.
          </SheetDescription>
        </SheetHeader>
        <ReorderBucket title="Critical — at or below minimum" items={reorder?.critical} tone="rose" />
        <ReorderBucket title="Will breach within lead time" items={reorder?.warning} tone="amber" />
        <ReorderBucket title="Watchlist (within 2× lead time)" items={reorder?.watch} tone="slate" />
      </SheetContent>
    </Sheet>
  );
}

function ReorderBucket({ title, items, tone }) {
  if (!items || items.length === 0) return null;
  const toneCls = tone === "rose" ? "text-rose-600 dark:text-rose-400"
    : tone === "amber" ? "text-amber-600 dark:text-amber-400"
    : "text-muted-foreground";
  return (
    <div className="mt-4 rounded-xl border border-border bg-card p-3" data-testid={`reorder-bucket-${tone}`}>
      <div className={`text-sm font-medium mb-2 flex items-center gap-1.5 ${toneCls}`}>
        <Package className="w-4 h-4" /> {title} <span className="ml-auto text-xs font-mono">{items.length}</span>
      </div>
      <div className="divide-y divide-border">
        {items.map((it) => (
          <div key={it.item_id} className="py-2 flex items-start gap-3 text-sm">
            <div className="min-w-0 flex-1">
              <div className="font-medium truncate">{it.name}</div>
              <div className="text-[11px] text-muted-foreground">
                <span className="font-mono">{it.sku}</span>
                {it.supplier_name && <span> • {it.supplier_name}</span>}
                {(it.lead_time_label || it.lead_time_days) && (
                  <span> • ⏱ {it.lead_time_label || `${it.lead_time_days} days`}</span>
                )}
                {it.daily_usage != null && <span> • {it.daily_usage}/day</span>}
              </div>
            </div>
            <div className="text-right shrink-0">
              <div className={`text-sm font-mono tabular-nums ${toneCls}`}>{it.days_of_cover}d cover</div>
              <div className="text-[11px] text-muted-foreground">{it.quantity} / {it.min_stock} min</div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

function WidgetCard({ icon: Icon, iconTone, title, subtitle, count, countTone, href, empty, children, testid }) {
  const navigate = useNavigate();
  const hasContent = React.Children.toArray(children).filter(Boolean).length > 0;
  return (
    <Link to={href} className="rounded-2xl border border-border bg-card p-4 hover:bg-accent/30 transition-colors flex flex-col" data-testid={testid}>
      <div className="flex items-center gap-2 mb-1 text-sm font-medium text-muted-foreground">
        <Icon className={`w-4 h-4 ${iconTone || "text-primary"}`} /> {title}
        <ChevronRight className="w-4 h-4 ml-auto text-muted-foreground" />
      </div>
      <div className="flex items-baseline gap-2">
        <div className={`text-3xl font-semibold tabular-nums ${countTone || ""}`} data-testid={`${testid}-count`}>{count}</div>
        <span className="text-xs text-muted-foreground">{subtitle}</span>
      </div>
      <div className="mt-2 flex-1 min-h-0">
        {hasContent ? children : (
          <div className="text-xs text-muted-foreground py-3">{empty}</div>
        )}
      </div>
    </Link>
  );
}
